import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import { hasSessionExpired } from '../api/client'
import { useAuthStore } from '../stores/auth'

declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    guestOnly?: boolean
    requiresAuth?: boolean
  }
}

// Lazy-loaded: each view lands in its own chunk instead of the main bundle,
// fetched only when its route is actually visited.
const LoginView = () => import('../views/LoginView.vue')
const ForgotPasswordView = () => import('../views/ForgotPasswordView.vue')
const ResetPasswordView = () => import('../views/ResetPasswordView.vue')
const AcceptInviteView = () => import('../views/AcceptInviteView.vue')
const DashboardView = () => import('../views/DashboardView.vue')
const DashboardFindingsView = () => import('@/views/dashboard/DashboardFindingsView.vue')
const IntegrationsView = () => import('../views/IntegrationsView.vue')
const ProjectsView = () => import('../views/ProjectsView.vue')
const ProjectView = () => import('../views/ProjectView.vue')
const PasswordSettingsView = () => import('../views/settings/PasswordSettingsView.vue')
const TimezoneSettingsView = () => import('../views/settings/TimezoneSettingsView.vue')
const GithubAccountSettingsView = () => import('../views/settings/GithubAccountSettingsView.vue')
const ClaudeAccountsSettingsView = () => import('../views/settings/ClaudeAccountsSettingsView.vue')
const TeamSettingsView = () => import('../views/settings/TeamSettingsView.vue')
const OpsSettingsView = () => import('@/views/settings/OpsSettingsView.vue')
const ReviewHistoryView = () => import('../views/ReviewHistoryView.vue')
const BuildsView = () => import('@/views/BuildsView.vue')
const LinearTaskSessionView = () => import('@/views/LinearTaskSessionView.vue')
const ReviewRunDetailView = () => import('@/views/ReviewRunDetailView.vue')
const PendingApprovalsView = () => import('../views/monitoring/PendingApprovalsView.vue')
const RepoScansView = () => import('../views/monitoring/RepoScansView.vue')
const AiUsageView = () => import('../views/monitoring/AiUsageView.vue')
const AiUsageLogDetailView = () => import('../views/monitoring/AiUsageLogDetailView.vue')
const QueueMonitorView = () => import('../views/monitoring/QueueMonitorView.vue')
const RuleFindingTrendsView = () => import('@/views/monitoring/RuleFindingTrendsView.vue')
const HelpView = () => import('../views/HelpView.vue')
const GithubRepositoriesView = () => import('../views/integrations/GithubRepositoriesView.vue')
const GithubRepositoryView = () => import('../views/integrations/GithubRepositoryView.vue')
const GithubRepositoryRuleView = () => import('../views/integrations/GithubRepositoryRuleView.vue')
const CodeGraphView = () => import('@/views/integrations/CodeGraphView.vue')
const ArchitectureView = () => import('../architecture/ArchitectureView.vue')
const OverviewView = () => import('@/views/OverviewView.vue')

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      redirect: '/login',
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      meta: { guestOnly: true, title: 'Login' },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPasswordView,
      meta: { guestOnly: true, title: 'Forgot Password' },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: ResetPasswordView,
      meta: { title: 'Reset Password' },
    },
    {
      path: '/accept-invite',
      name: 'accept-invite',
      component: AcceptInviteView,
      meta: { guestOnly: true, title: 'Accept Invite' },
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: DashboardView,
      meta: { requiresAuth: true, title: 'Dashboard' },
    },
    {
      path: '/dashboard/findings',
      name: 'dashboard-findings',
      component: DashboardFindingsView,
      meta: { requiresAuth: true, title: 'Findings & Rules' },
    },
    {
      path: '/projects',
      name: 'projects',
      component: ProjectsView,
      meta: { requiresAuth: true, title: 'Projects' },
    },
    {
      path: '/projects/:id',
      name: 'project',
      component: ProjectView,
      meta: { requiresAuth: true, title: 'Project' },
    },
    {
      path: '/integrations',
      name: 'integrations',
      component: IntegrationsView,
      meta: { requiresAuth: true, title: 'Integrations' },
    },
    {
      path: '/integrations/github/repositories',
      name: 'integrations-github-repositories',
      component: GithubRepositoriesView,
      meta: { requiresAuth: true, title: 'GitHub Repositories' },
    },
    {
      path: '/integrations/github/repositories/:id',
      name: 'integrations-github-repository-edit',
      component: GithubRepositoryView,
      meta: { requiresAuth: true, title: 'Edit GitHub Repository' },
    },
    {
      path: '/integrations/github/repositories/:repoId/code-graph',
      name: 'integrations-github-repository-code-graph',
      component: CodeGraphView,
      meta: { requiresAuth: true, title: 'Code Graph' },
    },
    {
      path: '/integrations/github/repositories/:repoId/rules/new',
      name: 'integrations-github-repository-rule-create',
      component: GithubRepositoryRuleView,
      meta: { requiresAuth: true, title: 'New Repository Rule' },
    },
    {
      path: '/integrations/github/repositories/:repoId/rules/:ruleId',
      name: 'integrations-github-repository-rule-edit',
      component: GithubRepositoryRuleView,
      meta: { requiresAuth: true, title: 'Edit Repository Rule' },
    },
    {
      path: '/review-history',
      name: 'review-history',
      component: ReviewHistoryView,
      meta: { requiresAuth: true, title: 'Review History' },
    },
    {
      path: '/review-history/:installationId/:id',
      name: 'review-run-detail',
      component: ReviewRunDetailView,
      meta: { requiresAuth: true, title: 'Review Run' },
    },
    {
      path: '/builds',
      name: 'builds',
      component: BuildsView,
      meta: { requiresAuth: true, title: 'Builds' },
    },
    {
      path: '/linear-tasks/:id',
      name: 'linear-task-session',
      component: LinearTaskSessionView,
      meta: { requiresAuth: true, title: 'Build Session' },
    },
    {
      path: '/~/architecture',
      name: 'architecture',
      component: ArchitectureView,
      meta: { requiresAuth: true, title: 'Architecture' },
    },
    {
      path: '/~/overview',
      name: 'overview',
      component: OverviewView,
      meta: { requiresAuth: true, title: 'Overview' },
    },
    {
      path: '/help',
      name: 'help',
      component: HelpView,
      meta: { requiresAuth: true, title: 'Help' },
    },
    {
      path: '/settings',
      redirect: '/settings/password',
    },
    {
      path: '/settings/password',
      name: 'settings-password',
      component: PasswordSettingsView,
      meta: { requiresAuth: true, title: 'Settings — Password' },
    },
    {
      path: '/settings/timezone',
      name: 'settings-timezone',
      component: TimezoneSettingsView,
      meta: { requiresAuth: true, title: 'Settings — Timezone' },
    },
    {
      path: '/settings/github',
      name: 'settings-github',
      component: GithubAccountSettingsView,
      meta: { requiresAuth: true, title: 'Settings — GitHub' },
    },
    {
      path: '/settings/claude-accounts',
      name: 'settings-claude-accounts',
      component: ClaudeAccountsSettingsView,
      meta: { requiresAuth: true },
    },
    {
      path: '/settings/team',
      name: 'settings-team',
      component: TeamSettingsView,
      meta: { requiresAuth: true, title: 'Settings — Team' },
    },
    {
      path: '/settings/ops',
      name: 'settings-ops',
      component: OpsSettingsView,
      meta: { requiresAuth: true, title: 'Settings — Ops' },
    },
    {
      path: '/monitoring',
      redirect: '/monitoring/repo-scans',
    },
    {
      path: '/monitoring/pending-approvals',
      name: 'monitoring-pending-approvals',
      component: PendingApprovalsView,
      meta: { requiresAuth: true, title: 'Pending Approvals' },
    },
    {
      path: '/monitoring/repo-scans',
      name: 'monitoring-repo-scans',
      component: RepoScansView,
      meta: { requiresAuth: true, title: 'Repo Scans' },
    },
    {
      path: '/monitoring/ai-usage',
      name: 'monitoring-ai-usage',
      component: AiUsageView,
      meta: { requiresAuth: true, title: 'AI Usage' },
    },
    {
      path: '/monitoring/ai-usage/:id',
      name: 'monitoring-ai-usage-detail',
      component: AiUsageLogDetailView,
      meta: { requiresAuth: true, title: 'AI Usage Log' },
    },
    {
      path: '/monitoring/queue-monitor',
      name: 'monitoring-queue-monitor',
      component: QueueMonitorView,
      meta: { requiresAuth: true, title: 'Queue Monitor' },
    },
    {
      path: '/monitoring/rule-trends',
      name: 'monitoring-rule-trends',
      component: RuleFindingTrendsView,
      meta: { requiresAuth: true, title: 'Rule Trends' },
    },
  ],
})

// Route chunks are content-hashed, so a deploy deletes the old files a
// still-open tab has in its router map — the next lazy-loaded navigation
// 404s (served as index.html, hence the "Expected a JavaScript-or-Wasm
// module script" error) instead of resolving. Recover by reloading straight
// to the target URL, which picks up the new build's index.html and chunk
// map. Guarded by sessionStorage so a genuinely broken chunk fails once
// instead of reload-looping forever.
const STALE_CHUNK_RELOAD_KEY = 'router:stale-chunk-reload'

// Extracted (rather than inlined in router.onError) so tests can exercise
// each branch directly instead of forcing a real dynamic-import failure.
export function handleStaleChunkError(error: Error, to: RouteLocationNormalized): void {
  if (!/dynamically imported module|Importing a module script failed/i.test(error.message)) {
    throw error
  }

  if (sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY)) {
    console.error('Failed to load chunk after reload; the build may be broken:', error)
    return
  }

  sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, '1')
  window.location.assign(to.fullPath)
}

router.onError(handleStaleChunkError)

router.afterEach(() => {
  sessionStorage.removeItem(STALE_CHUNK_RELOAD_KEY)
})

router.afterEach((to) => {
  document.title = to.meta.title ? `${to.meta.title} · TyphoonHQ` : 'TyphoonHQ'
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // Resolve the session once on first navigation (app-load probe). fetchUser()
  // rethrows on non-401 errors (e.g. a network blip or 500) — catch here so a
  // transient failure doesn't surface as an uncaught rejection and stall
  // navigation; auth.initialized is still set (in fetchUser()'s finally), so
  // the guard below falls through treating the session as unauthenticated.
  if (!auth.initialized) {
    try {
      await auth.fetchUser()
    } catch (e: unknown) {
      console.error('Failed to resolve the current session:', e)
    }
  } else if (auth.isAuthenticated && hasSessionExpired()) {
    // fetchUser() above only ever runs once per app load, so a session that
    // expires mid-visit (e.g. the tab sat idle/asleep past SESSION_LIFETIME)
    // would otherwise go unnoticed until some API call happened to 401. Catch
    // it here instead, on the navigation itself, so the very next interaction
    // drops the user back to /login rather than rendering stale authed UI.
    auth.reset()
  }

  const isAuth = auth.isAuthenticated

  if (to.meta.guestOnly) {
    return isAuth ? { name: 'dashboard' } : true
  }

  if (to.meta.requiresAuth && !isAuth) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  return true
})

export default router
